import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { KpiCard, PoolPill, PageHeader, SectionTitle } from "@/components/ui-extra";
import { BaseComparisonCards } from "@/components/BaseComparisonCards";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, Users, FileText, Truck, AlertTriangle, Send, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { safeFormatDate, safeDistanceToNow } from "@/lib/dates";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DailySummaryCard } from "@/components/DailySummaryCard";
import { WeatherBackdrop } from "@/components/WeatherBackdrop";

/**
 * Landing page fallback lookup — the first accessible page for a user
 * who lacks `dashboard:read`. Order matches the sidebar so the user
 * lands on whatever they'd naturally see first.
 */
const LANDING_FALLBACK = [
  { perm: "horses:read",    to: "/horses" },
  { perm: "rankings:read",  to: "/horses/rankings" },
  { perm: "breeding:read",  to: "/breeding" },
  { perm: "staff:read",     to: "/staff" },
  { perm: "inventory:read", to: "/inventory/feed" },
  { perm: "invoices:read",  to: "/invoices" },
];

export default function DashboardPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // "Recent WhatsApp activity" + "Activity" are master-admin only — every
  // other signed-in user gets the operational widgets without the audit feed.
  const { user, hasPerm, isMaster, loading: authLoading, permissionsLoaded } = useAuth();

  // ── Per-component permission flags ─────────────────────────────
  // `dashboard:read` gates page access; each widget below has its
  // own key so admins can hide individual cards from specific roles
  // via the Roles editor. Master admins bypass all sub-checks.
  //
  // KPI cards are one-per-permission so operators can (say) hide the
  // Horses count from a finance role while keeping the Invoices count
  // visible. Each card also resolves its click-through target
  // dynamically — a role granted `dashboard:view_kpi_horses` but NOT
  // `horses:read` will still be able to click through to whatever
  // horse-adjacent page they DO have access to (rankings / breeding).
  const canSeeKpiHorses = isMaster || hasPerm?.("dashboard:view_kpi_horses");
  const canSeeKpiStaff = isMaster || hasPerm?.("dashboard:view_kpi_staff");
  const canSeeKpiInvoices = isMaster || hasPerm?.("dashboard:view_kpi_invoices");
  const canSeeKpiFeedAlerts = isMaster || hasPerm?.("dashboard:view_kpi_feed_alerts");
  const canSeeAnyKpi = canSeeKpiHorses || canSeeKpiStaff || canSeeKpiInvoices || canSeeKpiFeedAlerts;
  const canSeeDailySummary = isMaster || hasPerm?.("dashboard:view_daily_summary");
  const canSeePoolDistribution = isMaster || hasPerm?.("dashboard:view_pool_distribution");
  const canSeeTopHorses = isMaster || hasPerm?.("dashboard:view_top_horses");
  const canSeeFeedAlertsCard = isMaster || hasPerm?.("dashboard:view_feed_alerts");
  const canSeeBotActivity = isMaster || hasPerm?.("dashboard:view_bot_activity");
  const canSeeAuditActivity = isMaster || hasPerm?.("dashboard:view_audit_activity");

  // ── Dynamic KPI click-through targets ──────────────────────────
  // The card links to the FIRST horse-related page the user has
  // access to. This means a user granted the Horses KPI without
  // `horses:read` can still click through — e.g. a viewer role could
  // be given the Horses count and click through to Rankings. If
  // nothing is accessible, the card renders as static (no `to` prop,
  // no cursor-pointer, no arrow icon).
  const horsesKpiHref =
    hasPerm?.("horses:read") ? "/horses"
    : hasPerm?.("rankings:read") ? "/horses/rankings"
    : hasPerm?.("breeding:read") ? "/breeding"
    : undefined;
  const staffKpiHref = hasPerm?.("staff:read") ? "/staff" : undefined;
  const invoicesKpiHref = hasPerm?.("invoices:read") ? "/invoices?view=outstanding" : undefined;
  const feedAlertsKpiHref = hasPerm?.("inventory:read") ? "/inventory/feed" : undefined;

  // Loader with one-shot retry on transient 5xx / network errors. New users
  // hit a brief window where the backend is still committing their user
  // record; without the retry, that surfaced as a "Failed to load
  // dashboard" flash. Auth errors (401/403) don't retry — those are
  // genuine permission problems and retrying just delays the redirect.
  const load = async () => {
    setLoadError(false);
    const attempt = async () => {
      const res = await api.get("/dashboard/summary");
      return res.data;
    };
    try {
      let body;
      try {
        body = await attempt();
      } catch (e) {
        const status = e?.response?.status;
        const retryable = !status || status >= 500;
        if (!retryable) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        body = await attempt();
      }
      setS(body);
    } catch (e) {
      console.error(e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Don't fire until AuthContext has resolved the user record. On
    // first-login the backend is still provisioning during this brief
    // window; firing early used to race the singleflight lock and
    // occasionally 403 before role/permissions were committed.
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);
  useQuickEditRefresh(load);

  // ── Auth settlement gate ────────────────────────────────────────
  // We MUST wait for both /auth/me and /permissions/catalog to resolve
  // before making any redirect decision. Otherwise the empty initial
  // `permissions` Set makes `hasPerm("dashboard:read")` return false
  // for a user who legitimately has it, and we <Navigate> them to
  // /settings → AdminRoute bounces them to "/" → this component runs
  // again with permissions STILL empty → Navigate again. That loop
  // hammers history.replaceState() and trips the browser's 100/10s
  // throttle (Sentry issue #508a82c3). The order of checks below is
  // load-bearing — do NOT reorder.
  if (authLoading || !permissionsLoaded) {
    return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading…</div>;
  }

  // Permission gate — users without `dashboard:read` get redirected to
  // whichever page they DO have access to. Master admins bypass all
  // checks. Only runs once permissions are guaranteed-loaded (above).
  const canSeeDashboard = isMaster || hasPerm?.("dashboard:read");
  if (!canSeeDashboard) {
    const fallback = LANDING_FALLBACK.find((f) => hasPerm?.(f.perm));
    // If the user has literally NO known permission, don't <Navigate>
    // to /settings — they can't render that either and we'd re-loop.
    // Show an explicit "no access" state instead so the loop can't form.
    if (!fallback) {
      return (
        <div className="rounded-xl border bg-card p-6 max-w-lg" data-testid="dashboard-no-access">
          <div className="text-sm text-destructive font-medium">No access</div>
          <div className="text-xs text-muted-foreground mt-1">
            Your account doesn't have any pages assigned yet. Ask a master admin
            to grant you a role with at least <code>dashboard:read</code>.
          </div>
        </div>
      );
    }
    return <Navigate to={fallback.to} replace />;
  }

  if (loading) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading…</div>;
  if (loadError || !s) {
    return (
      <div className="rounded-xl border bg-card p-6 max-w-lg" data-testid="dashboard-load-error">
        <div className="text-sm text-destructive font-medium">Failed to load dashboard</div>
        <div className="text-xs text-muted-foreground mt-1">
          This usually means your session is still warming up. Give it a moment and try again.
        </div>
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          className="mt-4 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
          data-testid="dashboard-retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  const poolData = Object.entries(s.horses.by_pool).map(([pool, count]) => ({ pool, count }));
  // SAST = UTC+2. We don't depend on the browser's local zone so the date
  // matches the data even if the operator is travelling.
  const sastNow = new Date(Date.now() + ((new Date().getTimezoneOffset() + 120) * 60_000));
  const todayLabel = format(sastNow, "EEEE, d MMMM yyyy"); // e.g. "Sunday, 22 June 2026"

  // When the user has `dashboard:read` but none of the per-widget
  // permissions, we'd render an empty page — not helpful. Show a
  // friendly "your admin hasn't enabled any Dashboard cards yet" note
  // instead so it's obvious the page loaded and the missing content
  // is a permissions decision, not a bug.
  const anyDashboardWidget =
    canSeeAnyKpi || canSeeDailySummary || canSeePoolDistribution ||
    canSeeTopHorses || canSeeFeedAlertsCard || canSeeBotActivity || canSeeAuditActivity;

  // ── Layout balancing ─────────────────────────────────────────────
  // The two-column body (Pool + Top-horses on the left, Feed alerts +
  // Bot activity + Audit on the right) needs to gracefully handle
  // roles that have widgets on ONLY one side. We count widgets per
  // column so the visible one can stretch to full width instead of
  // leaving an empty half. Same idea for the KPI row: count visible
  // tiles so the grid contracts from 4 → 3 → 2 → 1 columns rather
  // than showing empty slots.
  const visibleKpiCount =
    (canSeeKpiHorses ? 1 : 0) +
    (canSeeKpiStaff ? 1 : 0) +
    (canSeeKpiInvoices ? 1 : 0) +
    (canSeeKpiFeedAlerts ? 1 : 0);
  const hasLeftBody = canSeePoolDistribution || canSeeTopHorses;
  const hasRightBody = canSeeFeedAlertsCard || canSeeBotActivity || canSeeAuditActivity;

  return (
    <div className="space-y-8 relative">
      {/* Animated Apple-Weather-style backdrop. Fixed-position + `-z-10`
          so it fills the viewport behind the app shell content without
          intercepting clicks. Only rendered on this page — all other
          routes render the plain paper background. */}
      <WeatherBackdrop />
      {/* Sits on top of the weather backdrop (which is always a dark
          sky image or gradient). Force text to white — regardless of
          light/dark theme — so it stays legible over the backdrop
          without a scrim card breaking the intended cinematic look.
          The wrapper uses tailwind's descendant selector so every
          child (heading, subtitle, muted spans) inherits white. */}
      <div className="[&_*]:!text-white [&_h1]:!text-white">
        <PageHeader
          title="Operations overview"
          subtitle={
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{todayLabel}</span>
            </span>
          }
        />
      </div>

      {/* Empty state — page loaded but the user's role has no Dashboard
          widgets enabled. Prevents the page from looking broken. */}
      {!anyDashboardWidget && (
        <div
          className="rounded-xl border bg-card p-6 max-w-2xl"
          data-testid="dashboard-empty-widgets"
        >
          <div className="text-sm font-medium text-foreground">Dashboard cards are hidden for your role</div>
          <p className="text-xs text-muted-foreground mt-1">
            You have access to the Dashboard, but no individual cards are enabled for your role yet.
            Ask a master admin to grant you specific dashboard permissions in <span className="font-mono">Members &amp; Roles → Roles</span>
            {" "}(look for keys prefixed with <span className="font-mono">dashboard:view_</span>).
          </p>
        </div>
      )}

      {/* KPI Row — each card is gated on its own permission and links
          to a target that accords with the viewer's other permissions
          (see the *KpiHref variables above). Cards without an accessible
          target render as static tiles (no arrow, no cursor).

          ── Auto-sizing grid ──
          Instead of a fixed 4-column layout that leaves empty slots
          when some KPIs are hidden, we count the visible cards and
          pick a grid class so the remaining tiles ALWAYS fill the
          row edge-to-edge on both mobile and desktop. */}
      {canSeeAnyKpi && (
      <div className={`grid gap-4 ${kpiGridCols(visibleKpiCount)}`} data-testid="dashboard-kpi-row">
        {canSeeKpiHorses && (
          <KpiCard label="Horses" value={s.horses.total} icon={Activity}
            sub={`${(s.horses.by_pool.Elite || 0) + (s.horses.by_pool.Good || 0)} active, ${s.horses.by_pool.Unrated || 0} unrated`}
            to={horsesKpiHref}
            testId="dashboard-kpi-horses" />
        )}
        {canSeeKpiStaff && (
          <KpiCard label="Staff" value={s.staff.total} icon={Users}
            sub={s.staff.pending_approval > 0 ? `${s.staff.pending_approval} pending approval` : "all approved"}
            tone={s.staff.pending_approval > 0 ? "alert" : "default"}
            to={staffKpiHref}
            testId="dashboard-kpi-staff" />
        )}
        {canSeeKpiInvoices && (
          <KpiCard label="Outstanding invoices" value={s.invoices.pending} icon={FileText}
            sub={`${s.invoices.verified} verified`}
            tone={s.invoices.pending > 0 ? "alert" : "good"}
            to={invoicesKpiHref}
            testId="dashboard-kpi-invoices" />
        )}
        {canSeeKpiFeedAlerts && (
          <KpiCard label="Feed alerts" value={s.feed_alerts.length} icon={AlertTriangle}
            sub={s.feed_alerts.length === 0 ? "All items healthy" : `${s.feed_alerts.length} item${s.feed_alerts.length>1?"s":""} low`}
            tone={s.feed_alerts.length > 0 ? "alert" : "good"}
            to={feedAlertsKpiHref}
            testId="dashboard-kpi-feed-alerts" />
        )}
      </div>
      )}

      {/* Per-base comparison (all-access users, multi-base only) */}
      <BaseComparisonCards />

      {/* Daily summary + bill follow-ups */}
      {canSeeDailySummary && <DailySummaryCard />}

      {/* Two-column body — collapses to single-column when only ONE
          side has content, so the visible column fills the full width
          instead of leaving an empty half. If both sides are empty
          we skip the wrapper entirely. */}
      {(hasLeftBody || hasRightBody) && (
      <div className="grid lg:grid-cols-12 gap-6">
        {hasLeftBody && (
        <div className={`space-y-6 ${hasRightBody ? "lg:col-span-8" : "lg:col-span-12"}`} data-testid="dashboard-left-column">
          {/* Pool distribution */}
          {canSeePoolDistribution && (
          <Card className="rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-medium">Horse pool distribution</CardTitle>
                <p className="text-xs text-muted-foreground">Formula v9 score → pool mapping</p>
              </div>
              <Link to="/horses/rankings" className="text-xs text-[hsl(var(--ap-forest))] hover:underline inline-flex items-center gap-1" data-testid="dashboard-rankings-link">
                See full leaderboard <ArrowRight className="h-3 w-3" />
              </Link>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]" data-testid="dashboard-pool-chart">
                <ResponsiveContainer>
                  <BarChart data={poolData}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="pool" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Top horses */}
          {canSeeTopHorses && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-medium">Top horses by score</CardTitle>
              <p className="text-xs text-muted-foreground">Updated after every horse ridden</p>
            </CardHeader>
            <CardContent>
              {s.top_horses.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No horses rated yet. Use <span className="font-mono">rate</span> on WhatsApp to start.</p>
              ) : (
                <div className="space-y-2" data-testid="dashboard-top-horses">
                  {s.top_horses.map((h, i) => (
                    <Link to={`/horses/${h.id}`} key={h.id}
                      className="flex items-center justify-between p-3 rounded-md hover:bg-accent transition-colors group">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-muted-foreground w-5">{i + 1}</span>
                        <span className="font-medium">{h.name}</span>
                        <PoolPill pool={h.ranking?.pool} stars={h.ranking?.stars} />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-serif text-xl tabular-nums">{h.ranking?.score?.toFixed?.(1) ?? 0}</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
        )}

        {/* RIGHT — collapses to full width when the left column is
            empty. When the right column itself is empty we skip it
            entirely (the wrapping `{(hasLeftBody || hasRightBody) &&` above
            already handles the case where BOTH columns are empty). */}
        {hasRightBody && (
        <div className={`space-y-6 ${hasLeftBody ? "lg:col-span-4" : "lg:col-span-12"}`} data-testid="dashboard-right-column">
          {/* Feed alerts */}
          {canSeeFeedAlertsCard && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--ap-warning))]" />
                Feed alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {s.feed_alerts.length === 0 ? (
                <p className="text-sm text-muted-foreground">All feed levels healthy.</p>
              ) : (
                <div className="space-y-3" data-testid="dashboard-feed-alerts">
                  {s.feed_alerts.map((a) => (
                    <Link to="/inventory/feed" key={a.item} className="block p-3 rounded-md border bg-[hsl(var(--ap-warning)/0.04)] hover:bg-[hsl(var(--ap-warning)/0.08)] transition-colors">
                      <div className="flex items-baseline justify-between">
                        <span className="font-medium capitalize">{a.item}</span>
                        <span className="text-sm font-mono text-[hsl(var(--ap-warning))]">{a.days_until_empty} days left</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{a.current_qty} {a.unit} on hand. ~{a.avg_daily_use}/day</p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Recent bot activity (WhatsApp primary, Telegram legacy).
              Gated on `dashboard:view_bot_activity` — master-only by
              default via implicit-all, but the perm can be granted to
              any role via the Roles editor. */}
          {canSeeBotActivity && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Send className="h-4 w-4 text-[hsl(var(--ap-info))]" />
                Recent WhatsApp activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(s.recent_bot_ingestions || s.recent_telegram_ingestions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent bot activity yet.</p>
              ) : (
                <div className="space-y-2 max-h-[260px] overflow-auto" data-testid="dashboard-recent-bot-activity">
                  {(s.recent_bot_ingestions || s.recent_telegram_ingestions).map((t) => (
                    <div key={t.id} className="text-xs p-2 rounded-md hover:bg-accent">
                      <div className="flex items-baseline justify-between">
                        <span className="font-medium">{t.actor}</span>
                        <span className="text-muted-foreground">{safeDistanceToNow(t.created_at)} ago</span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 truncate">
                        {t.entity === "whatsapp" ? "WhatsApp" : "Telegram"}
                        {t.after?.text ? ` · ${t.after.text}` : ""}
                        {t.after?.type && !t.after?.text ? `: ${t.after.type}` : ""}
                        {t.after?.supplier ? `: ${t.after.supplier}` : ""}
                        {t.after?.name ? `: ${t.after.name}` : ""}
                        {t.after?.horse ? `: ${t.after.horse}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Recent audit. Gated on `dashboard:view_audit_activity` —
              master-only by default via implicit-all, but grantable to
              any role via the Roles editor. */}
          {canSeeAuditActivity && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-medium">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[260px] overflow-auto" data-testid="dashboard-recent-audit">
                {s.recent_audit.slice(0, 10).map((a) => (
                  <div key={a.id} className="text-xs flex items-start gap-2 py-1">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[hsl(var(--ap-brass))]" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between">
                        <span><span className="font-medium">{a.actor}</span> <span className="text-muted-foreground">{a.action}</span> <span className="font-mono text-[10px]">{a.entity}</span></span>
                      </div>
                      <div className="text-muted-foreground">{safeFormatDate(a.created_at, "MMM d, HH:mm")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          )}
        </div>
        )}
      </div>
      )}
    </div>
  );
}

/**
 * Pick a Tailwind grid-cols class based on how many KPI cards are
 * visible so the row always fills edge-to-edge on every viewport.
 * We hard-code the classes (rather than interpolating `grid-cols-${n}`)
 * because Tailwind's JIT purges any class that isn't literally present
 * in the source — dynamic class names get stripped from production.
 */
function kpiGridCols(count) {
  switch (count) {
    case 1: return "grid-cols-1";
    case 2: return "grid-cols-1 sm:grid-cols-2";
    case 3: return "grid-cols-2 lg:grid-cols-3";
    case 4:
    default:
      return "grid-cols-2 lg:grid-cols-4";
  }
}
